import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

/**
 * EAA-1756: thin wrapper over oidc-client-ts's `UserManager` — the frozen
 * auth-client API per design-frontend.md `packages/auth-client`
 * (`createEmgAuth` -> `{ login, handleCallback, getAccessToken, getClaims,
 * logout, onSessionEnded }`). Authorization Code + PKCE (oidc-client-ts
 * defaults a public client to S256), refresh via rotated refresh tokens
 * (`automaticSilentRenew`), and state kept in `sessionStorage` — per-tab,
 * NEVER `localStorage` — so switching sessions in one tab never bleeds into
 * another.
 */

export interface EmgAuthConfig {
    authority: string;
    clientId: string;
    redirectUri: string;
}

export interface EmgAuthClaims {
    sub: string;
    email?: string;
    name?: string;
    sid?: string;
}

export interface EmgAuthClient {
    login(returnTo?: string): Promise<void>;
    handleCallback(): Promise<User>;
    getAccessToken(): Promise<string | null>;
    getClaims(): Promise<EmgAuthClaims | null>;
    logout(): Promise<void>;
    onSessionEnded(cb: () => void): () => void;
    /** EAA-1825: fires with the new access token after every (silent) renewal. */
    onUserLoaded(cb: (accessToken: string) => void): () => void;
    /** EAA-1825: force a silent renew now; `null` when the session cannot be renewed. */
    refreshAccessToken(): Promise<string | null>;
}

export function createEmgAuth(config: EmgAuthConfig): EmgAuthClient {
    const origin = window.location.origin;

    const userManager = new UserManager({
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        post_logout_redirect_uri: `${origin}/`,
        response_type: 'code',
        scope: 'openid profile email',
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    async function login(returnTo?: string): Promise<void> {
        await userManager.signinRedirect({
            state: { returnTo: returnTo ?? '/' },
        });
    }

    async function handleCallback(): Promise<User> {
        return userManager.signinRedirectCallback();
    }

    async function getAccessToken(): Promise<string | null> {
        const user = await userManager.getUser();
        if (!user || user.expired) return null;
        return user.access_token;
    }

    async function getClaims(): Promise<EmgAuthClaims | null> {
        const user = await userManager.getUser();
        if (!user || user.expired || !user.profile.sub) return null;
        const { sub, email, name, sid } = user.profile;
        return { sub, email, name, sid };
    }

    async function logout(): Promise<void> {
        await userManager.signoutRedirect();
    }

    function onSessionEnded(cb: () => void): () => void {
        userManager.events.addUserSignedOut(cb);
        userManager.events.addUserSessionChanged(cb);
        userManager.events.addAccessTokenExpired(cb);
        return () => {
            userManager.events.removeUserSignedOut(cb);
            userManager.events.removeUserSessionChanged(cb);
            userManager.events.removeAccessTokenExpired(cb);
        };
    }

    // EAA-1825: `automaticSilentRenew` replaces the token in the UserManager's store,
    // but nothing told React. `AuthProvider` used to read the token once per route
    // change, so after the realm's access-token lifespan every request carried a token
    // Keycloak had already expired (portal-api 401, code 1006) until the operator hit
    // F5. This event is how the provider keeps its copy current.
    function onUserLoaded(cb: (accessToken: string) => void): () => void {
        const handler = (user: User) => {
            if (!user.expired && user.access_token) cb(user.access_token);
        };
        userManager.events.addUserLoaded(handler);
        return () => userManager.events.removeUserLoaded(handler);
    }

    async function refreshAccessToken(): Promise<string | null> {
        try {
            const user = await userManager.signinSilent();
            if (!user || user.expired) return null;
            return user.access_token;
        } catch {
            return null;
        }
    }

    return {
        login,
        handleCallback,
        getAccessToken,
        getClaims,
        logout,
        onSessionEnded,
        onUserLoaded,
        refreshAccessToken,
    };
}
